import { EventEmitter } from 'eventemitter3';

export type AudioStreamingEventMap = {
  'playback-started': () => void;
  'playback-stopped': () => void;
  'buffer-status': (status: { samplesAvailable: number; bufferUtilization: number }) => void;
  'error': (error: Error) => void;
};

export class AudioStreamingService extends EventEmitter<AudioStreamingEventMap> {
  private static instance: AudioStreamingService;
  private audioContext: AudioContext | null = null;
  private audioQueue: AudioBuffer[] = [];
  private isPlaying: boolean = false;
  private currentSource: AudioBufferSourceNode | null = null;
  private scheduledSources: AudioBufferSourceNode[] = [];
  private nextStartTime: number = 0;
  private sampleRate: number = 24000; // Default sample rate for PCM audio
  private audioWorkletNode: AudioWorkletNode | null = null;
  private isDestroyed: boolean = false;
  private playbackStartTime: number = 0;
  private totalSamplesPlayed: number = 0;
  private analyserNode: AnalyserNode | null = null;
  private gainNode: GainNode | null = null;
  
  // Version tracking for handling React StrictMode double-mount
  private initVersion = 0;
  private activeVersion = 0;

  private constructor() {
    super();
  }

  public static getInstance(): AudioStreamingService {
    if (!AudioStreamingService.instance || AudioStreamingService.instance.isDestroyed) {
      AudioStreamingService.instance = new AudioStreamingService();
    }
    return AudioStreamingService.instance;
  }

  public async initialize(sampleRate: number = 24000): Promise<void> {
    // Get a version for this initialization
    const myVersion = ++this.initVersion;
    
    // Always reset destroyed flag when initializing
    this.isDestroyed = false;

    this.sampleRate = sampleRate;
    
    // Reset state for new call
    this.isPlaying = false;
    this.audioQueue = [];
    this.nextStartTime = 0;
    this.playbackStartTime = 0;
    this.totalSamplesPlayed = 0;
    this.currentSource = null;
    
    // Check if we're still the latest version
    if (myVersion !== this.initVersion) {
      return;
    }
    
    // Always create a fresh audio context for new calls
    // Close existing context if it exists
    if (this.audioContext && this.audioContext.state !== 'closed') {
      await this.audioContext.close();
    }
    
    // Disconnect existing worklet if any
    if (this.audioWorkletNode) {
      this.audioWorkletNode.disconnect();
      this.audioWorkletNode = null;
    }
    
    // Disconnect existing analyser if any
    if (this.analyserNode) {
      this.analyserNode.disconnect();
      this.analyserNode = null;
    }
    
    // Disconnect existing gain if any
    if (this.gainNode) {
      this.gainNode.disconnect();
      this.gainNode = null;
    }
    
    // Create new audio context
    try {
      this.audioContext = new AudioContext({ sampleRate: this.sampleRate });
    } catch (e) {
      this.audioContext = new AudioContext();
    }
    
    // Resume if suspended
    if (this.audioContext.state === 'suspended') {
      await this.audioContext.resume();
    }
    
    // Check if we're still the latest version after AudioContext creation
    if (myVersion !== this.initVersion) {
      if (this.audioContext && this.audioContext.state !== 'closed') {
        await this.audioContext.close();
      }
      return;
    }
    
    const audioContext = this.audioContext;

    // Initialize audio worklet for real-time streaming if supported
    if ('audioWorklet' in audioContext) {
      try {
        // Create a simple processor for PCM streaming
        const processorCode = `
          class PCMProcessor extends AudioWorkletProcessor {
            constructor() {
              super();
              this.pcmBuffer = []; // Array to queue PCM chunks
              this.minBufferSize = 4800; // Minimum samples before starting playback (~100ms at 48kHz)
              this.isPlaying = false;
              this.hasStartedPlayback = false; // Track if we've ever started playing
              this.totalSamplesReceived = 0;
              this.totalSamplesPlayed = 0;
              this.expectedNextChunkIndex = 0;
              this.chunksReceived = 0;
              
              this.port.onmessage = (event) => {
                if (event.data.type === 'pcm') {
                  // Append PCM data to buffer instead of overwriting
                  const newData = event.data.data;
                  const chunkIndex = event.data.chunkIndex;
                  
                  // Log chunk receipt
                  this.chunksReceived++;
                  // Received chunk \${chunkIndex !== undefined ? chunkIndex : 'unknown'}
                  
                  // Check for out-of-order chunks
                  if (chunkIndex !== undefined && this.expectedNextChunkIndex !== 0 && chunkIndex !== this.expectedNextChunkIndex) {
                    // Chunk sequence gap detected
                  }
                  
                  // Update expected next chunk
                  if (chunkIndex !== undefined) {
                    this.expectedNextChunkIndex = chunkIndex + 1;
                  }
                  
                  
                  // Add new chunk to buffer
                  this.pcmBuffer.push(newData);
                  this.totalSamplesReceived += newData.length;
                  
                  // Calculate total buffered samples
                  const totalBufferedSamples = this.pcmBuffer.reduce((sum, chunk) => sum + chunk.length, 0);
                  
                  // Check if we should start playback
                  if (!this.hasStartedPlayback && totalBufferedSamples >= this.minBufferSize) {
                    this.hasStartedPlayback = true;
                    // Starting playback with sufficient buffer
                  }
                  
                  // Report buffer status
                  this.port.postMessage({
                    type: 'buffer-status',
                    samplesAvailable: totalBufferedSamples,
                    totalReceived: this.totalSamplesReceived,
                    totalPlayed: this.totalSamplesPlayed,
                    chunksReceived: this.chunksReceived,
                    hasStartedPlayback: this.hasStartedPlayback
                  });
                } else if (event.data.type === 'stop') {
                  // Clear buffer on stop
                  // Stop requested
                  this.pcmBuffer = [];
                  this.isPlaying = false;
                  this.hasStartedPlayback = false;
                  this.totalSamplesReceived = 0;
                  this.totalSamplesPlayed = 0;
                  this.expectedNextChunkIndex = 0;
                  this.chunksReceived = 0;
                }
              };
            }

            process(inputs, outputs, parameters) {
              const output = outputs[0];
              const channel = output[0];
              let outputIndex = 0;
              
              // Only start playing once we have minimum buffer
              if (!this.hasStartedPlayback) {
                // Fill with silence until we have enough buffered
                channel.fill(0);
                return true;
              }
              
              // Check if we have data to play
              const hasData = this.pcmBuffer.length > 0 && this.pcmBuffer.some(chunk => chunk.length > 0);
              
              // Update playing state
              if (hasData && !this.isPlaying) {
                this.isPlaying = true;
                this.port.postMessage({ type: 'playback-started' });
              } else if (!hasData && this.isPlaying) {
                this.isPlaying = false;
                this.port.postMessage({ type: 'playback-stopped' });
                // Playback stopped
              }
              
              // Process samples from buffer chunks
              let samplesPlayedThisFrame = 0;
              while (outputIndex < channel.length && this.pcmBuffer.length > 0) {
                const currentChunk = this.pcmBuffer[0];
                const remainingSamples = channel.length - outputIndex;
                const samplesToProcess = Math.min(remainingSamples, currentChunk.length);
                
                // Copy samples to output
                for (let i = 0; i < samplesToProcess; i++) {
                  channel[outputIndex++] = currentChunk[i];
                }
                
                samplesPlayedThisFrame += samplesToProcess;
                this.totalSamplesPlayed += samplesToProcess;
                
                // Remove processed samples from current chunk
                if (samplesToProcess === currentChunk.length) {
                  // Entire chunk processed, remove it
                  this.pcmBuffer.shift();
                } else {
                  // Partial chunk processed, update it
                  this.pcmBuffer[0] = currentChunk.slice(samplesToProcess);
                }
              }
              
              // Log if we played samples this frame
              if (samplesPlayedThisFrame > 0 && this.totalSamplesPlayed % 48000 < samplesPlayedThisFrame) {
                // Log approximately once per second
                // Playback progress update
              }
              
              // Fill remaining output with silence if needed
              while (outputIndex < channel.length) {
                channel[outputIndex++] = 0;
              }
              
              return true; // Keep processor alive
            }
          }
          
          registerProcessor('pcm-processor', PCMProcessor);
        `;

        const blob = new Blob([processorCode], { type: 'application/javascript' });
        const url = URL.createObjectURL(blob);
        await audioContext.audioWorklet.addModule(url);
        URL.revokeObjectURL(url);

        this.audioWorkletNode = new AudioWorkletNode(audioContext, 'pcm-processor');
        
        // Create gain node for volume control (set to 1.0 for normal volume)
        this.gainNode = audioContext.createGain();
        this.gainNode.gain.value = 1.0;
        
        // Create analyser node for audio level detection
        this.analyserNode = audioContext.createAnalyser();
        this.analyserNode.fftSize = 256; // Small FFT for fast response
        this.analyserNode.smoothingTimeConstant = 0.3; // Some smoothing for less jittery values
        
        // Connect: audioWorkletNode → gainNode → analyserNode → destination
        this.audioWorkletNode.connect(this.gainNode);
        this.gainNode.connect(this.analyserNode);
        this.analyserNode.connect(audioContext.destination);
        // AudioWorklet created and connected through analyser to destination
        
        // Set up message handling from the worklet
        this.audioWorkletNode.port.onmessage = (event) => {
          const { type, ...data } = event.data;
          
          switch (type) {
            case 'playback-started':
              this.isPlaying = true;
              this.playbackStartTime = audioContext.currentTime;
              this.emit('playback-started');
              break;
            case 'playback-stopped':
              this.isPlaying = false;
              this.emit('playback-stopped');
              break;
            case 'buffer-status':
              // Enhanced logging for buffer status
              const status = data as { 
                samplesAvailable: number; 
                bufferUtilization: number;
                totalReceived?: number;
                totalPlayed?: number;
                chunksReceived?: number;
                hasStartedPlayback?: boolean;
              };
              
              // Emit buffer status without logging
              
              this.emit('buffer-status', status);
              break;
            case 'error':
              this.emit('error', new Error(data.message || 'Audio worklet error'));
              break;
          }
        };
      } catch (error) {
        this.emit('error', error as Error);
      }
    }
    
    // Final version check and mark as active
    if (myVersion === this.initVersion) {
      this.activeVersion = myVersion;
    }
  }

  public async addPCMData(pcmData: ArrayBuffer | Float32Array | string, chunkIndex?: number): Promise<void> {
    if (this.isDestroyed) {
      return;
    }

    if (!this.audioContext || this.audioContext.state === 'closed') {
      return;
    }

    // Ensure audio context is resumed
    if (this.audioContext.state === 'suspended') {
      await this.audioContext.resume();
    }

    const audioContext = this.audioContext;

    // Convert input data to Float32Array
    let floatData: Float32Array;
    if (typeof pcmData === 'string') {
      // Base64 encoded PCM data
      const binaryString = atob(pcmData);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      // Assuming 16-bit PCM data
      const int16Array = new Int16Array(bytes.buffer);
      floatData = new Float32Array(int16Array.length);
      
      // Convert 16-bit PCM to float32 (-1 to 1 range)
      for (let i = 0; i < int16Array.length; i++) {
        floatData[i] = int16Array[i] / 32768.0;
      }
    } else if (pcmData instanceof ArrayBuffer) {
      // Assuming 16-bit PCM data
      const int16Array = new Int16Array(pcmData);
      floatData = new Float32Array(int16Array.length);
      
      // Convert 16-bit PCM to float32 (-1 to 1 range)
      for (let i = 0; i < int16Array.length; i++) {
        floatData[i] = int16Array[i] / 32768.0;
      }
    } else {
      floatData = pcmData;
    }

    // If we have an audio worklet, use it for lower latency
    if (this.audioWorkletNode) {
      // Sending audio data to AudioWorklet
      this.audioWorkletNode.port.postMessage({
        type: 'pcm',
        data: floatData,
        chunkIndex: chunkIndex
      });
    } else {
      // Fallback to buffer-based playback
      // Using buffer-based playback
      const audioBuffer = audioContext.createBuffer(1, floatData.length, this.sampleRate);
      audioBuffer.copyToChannel(floatData, 0);
      this.audioQueue.push(audioBuffer);
      
      if (!this.isPlaying) {
        this.startPlayback();
      }
    }
  }

  private startPlayback(): void {
    if (this.isDestroyed || this.isPlaying || !this.audioContext) {
      return;
    }
    
    this.isPlaying = true;
    this.nextStartTime = this.audioContext.currentTime;
    this.playbackStartTime = this.audioContext.currentTime;
    this.totalSamplesPlayed = 0;
    this.scheduleNextBuffer();
    this.emit('playback-started');
  }

  private scheduleNextBuffer(): void {
    if (this.isDestroyed || !this.isPlaying || this.audioQueue.length === 0 || !this.audioContext) {
      this.isPlaying = false;
      if (this.audioQueue.length === 0) {
        this.emit('playback-stopped');
      }
      return;
    }

    const audioContext = this.audioContext;
    const buffer = this.audioQueue.shift()!;
    
    const source = audioContext.createBufferSource();
    source.buffer = buffer;
    
    // Connect through gain and analyser if available, otherwise direct to destination
    if (this.gainNode && this.analyserNode) {
      source.connect(this.gainNode);
      // gainNode → analyserNode → destination already connected in initialize
    } else {
      source.connect(audioContext.destination);
    }
    
    source.onended = () => {
      // Remove from scheduled sources
      const index = this.scheduledSources.indexOf(source);
      if (index > -1) {
        this.scheduledSources.splice(index, 1);
      }
      if (this.currentSource === source) {
        this.currentSource = null;
      }
      this.scheduleNextBuffer();
    };

    const startTime = Math.max(this.nextStartTime, audioContext.currentTime);
    source.start(startTime);
    
    this.currentSource = source;
    this.scheduledSources.push(source);
    this.nextStartTime = startTime + buffer.duration;
  }

  public stop(): void {
    this.isPlaying = false;
    
    // Stop all scheduled sources
    for (const source of this.scheduledSources) {
      try {
        source.stop();
        source.disconnect();
      } catch (error) {
        // Source might already be stopped
      }
    }
    this.scheduledSources = [];
    this.currentSource = null;

    if (this.audioWorkletNode) {
      // Clear PCM buffer in the worklet
      this.audioWorkletNode.port.postMessage({ type: 'stop' });
    }

    this.audioQueue = [];
    this.nextStartTime = 0;
    this.emit('playback-stopped');
  }

  /**
   * Force stop all audio immediately
   * More aggressive than stop() - ensures complete audio termination
   */
  public async stopAllAudio(): Promise<void> {
    // Stop and disconnect current source
    if (this.currentSource) {
      try {
        this.currentSource.stop(0); // Stop immediately
        this.currentSource.disconnect();
      } catch (e) {
        // Ignore errors if already stopped
      }
      this.currentSource = null;
    }
    
    // Stop all scheduled sources
    for (const source of this.scheduledSources) {
      try {
        source.stop(0); // Stop immediately
        source.disconnect();
      } catch (e) {
        // Ignore errors if already stopped
      }
    }
    this.scheduledSources = [];
    
    // Clear the entire audio queue
    this.audioQueue = [];
    
    // Reset playback state
    this.isPlaying = false;
    this.nextStartTime = 0;
    this.playbackStartTime = 0;
    this.totalSamplesPlayed = 0;
    
    // Stop any worklet processing
    if (this.audioWorkletNode) {
      this.audioWorkletNode.port.postMessage({ type: 'stop' });
    }
    
    // Suspend audio context briefly to ensure all audio stops
    if (this.audioContext && this.audioContext.state === 'running') {
      await this.audioContext.suspend();
      // Resume after a brief delay
      setTimeout(() => {
        if (this.audioContext && this.audioContext.state === 'suspended') {
          this.audioContext.resume();
        }
      }, 50);
    }
    
    // Emit stop event
    this.emit('playback-stopped');
  }

  /**
   * Reset the service state without destroying the singleton instance
   * This is used for reconnection scenarios to ensure clean state
   */
  public reset(): void {
    // 1. STOP ALL AUDIO PLAYBACK - always call stop() regardless of isPlaying state
    // This ensures all scheduled sources are stopped
    this.stop();
    
    // 2. CLEAR AUDIO QUEUE
    this.audioQueue = [];
    
    // 3. DISCONNECT AUDIO NODES
    // Stop all scheduled sources
    for (const source of this.scheduledSources) {
      try {
        source.stop();
        source.disconnect();
      } catch (e) {
        // Ignore errors if already stopped
      }
    }
    this.scheduledSources = [];
    this.currentSource = null;
    
    if (this.gainNode) {
      this.gainNode.disconnect();
      this.gainNode = null;
    }
    
    if (this.analyserNode) {
      this.analyserNode.disconnect();
      this.analyserNode = null;
    }
    
    if (this.audioWorkletNode) {
      this.audioWorkletNode.disconnect();
      this.audioWorkletNode = null;
    }
    
    // 4. SUSPEND AUDIO CONTEXT (don't close, just suspend for reuse)
    if (this.audioContext && this.audioContext.state === 'running') {
      this.audioContext.suspend().catch(error => {
        // Ignore suspension errors
      });
      // Keep the audioContext reference for reuse
    }
    
    // 5. RESET STATE FLAGS
    this.isPlaying = false;
    this.nextStartTime = 0;
    this.playbackStartTime = 0;
    this.totalSamplesPlayed = 0;
    
    // 6. RESET VERSION TRACKING
    this.initVersion = 0;
    this.activeVersion = 0;
    
    // 7. MARK AS READY FOR REUSE (before removing listeners)
    this.isDestroyed = false;  // Important! Mark as NOT destroyed
    
    // 8. CLEAR EVENT LISTENERS (do this last to avoid race conditions)
    this.removeAllListeners();
  }
  
  /**
   * Check if the service is currently in use
   */
  public isInUse(): boolean {
    return this.isPlaying || this.audioQueue.length > 0 || !!this.audioWorkletNode;
  }
  
  /**
   * Destroy the service completely (deprecated - use reset() for reconnection)
   */
  public destroy(): void {
    // First reset everything
    this.reset();
    
    // Now actually close the AudioContext
    if (this.audioContext && this.audioContext.state !== 'closed') {
      this.audioContext.close().catch(error => {
        // Ignore close errors
      });
      this.audioContext = null;
    }
    
    // Then mark as truly destroyed (for singleton replacement)
    this.isDestroyed = true;
  }

  /**
   * Get the current audio output level
   * @returns A value between 0 and 1 representing the audio amplitude
   */
  public getCurrentAudioLevel(): number {
    if (!this.analyserNode || !this.isPlaying) {
      return 0;
    }
    
    try {
      const dataArray = new Uint8Array(this.analyserNode.frequencyBinCount);
      this.analyserNode.getByteFrequencyData(dataArray);
      
      // Calculate RMS (Root Mean Square) for better amplitude representation
      let sum = 0;
      for (let i = 0; i < dataArray.length; i++) {
        const normalized = dataArray[i] / 255;
        sum += normalized * normalized;
      }
      const rms = Math.sqrt(sum / dataArray.length);
      
      // Apply some scaling and smoothing
      // Scale up slightly as agent audio tends to be quieter than mic input
      const scaled = Math.min(1, rms * 2.5);
      
      return scaled;
    } catch (error) {
      // Silently handle audio level errors
      return 0;
    }
  }
  
  public isActive(): boolean {
    // For AudioWorklet, we rely on the events it sends
    // If we have an active worklet node, it's potentially active
    if (this.audioWorkletNode) {
      // The worklet will emit playback-started/stopped events
      // We track state based on those events
      return this.isPlaying;
    }
    // For buffer-based playback
    return this.isPlaying || this.audioQueue.length > 0;
  }

  public getCurrentPlaybackPosition(): number {
    if (!this.isPlaying || !this.audioContext) {
      return 0;
    }
    
    // Calculate position in milliseconds based on samples played
    const currentTime = this.audioContext.currentTime;
    const elapsedTime = currentTime - this.playbackStartTime;
    const samplesPlayed = elapsedTime * this.sampleRate;
    return Math.floor((samplesPlayed / this.sampleRate) * 1000);
  }

}

